import { useCallback, useEffect, useRef, useState } from 'react';
import { backingPage, impositionPlan, pageInfo, printOrder, type Issue, type PageLayout, type ProjectDoc } from '@cal/engine';
import { ImpositionNote } from './ProductStage';
import { ApiError, del, get, post, errorMessage } from '../api';
import { Field, Modal, NumberField, Spinner } from '../ui/controls';
import { toast } from '../ui/toast';
import { edit, flushPendingSave, save, useEditor } from './store';

interface ExportRow { id: string; version: number; kind: 'proof' | 'production' | 'pages' | 'backing' | 'imposed'; status: string; progress: number; message: string; fileName: string | null; bytes: number | null; durationMs: number | null; createdAt: string; hasFile: boolean }
interface Revision { version: number; kind: string; name: string | null; createdAt: string; createdBy: string | null }
interface HistoryEvent { type: string; version: number | null; note: string; createdAt: string; userName: string | null }

const STATUS: Record<string, { label: string; cls: string }> = {
  draft: { label: 'Draft', cls: '' },
  proof_generated: { label: 'Proof generated', cls: 'info' },
  changes_requested: { label: 'Changes requested', cls: 'warn' },
  approved: { label: 'Approved', cls: 'ok' },
  exported: { label: 'Exported', cls: 'ok' },
};
const KIND_LABEL = { proof: 'Customer proof PDF', production: 'Production PDF (single-up)', pages: 'Separate page PDFs (ZIP)', backing: 'Backing / reverse / carrier PDF', imposed: 'Imposed SRA3 PDF' };

export function ReviewStage({ doc, issues, layouts }: { doc: ProjectDoc; issues: Issue[]; layouts: Map<string, PageLayout> }) {
  const project = useEditor((s) => s.project)!;
  const saveState = useEditor((s) => s.saveState);
  const acked = new Set(doc.checks.acknowledged);
  const errors = issues.filter((i) => i.severity === 'error');
  const warnings = issues.filter((i) => i.severity === 'warning');
  const unacked = warnings.filter((w) => !acked.has(w.id));
  const [exports, setExports] = useState<ExportRow[]>([]);
  const [history, setHistory] = useState<{ revisions: Revision[]; events: HistoryEvent[] }>({ revisions: [], events: [] });
  const [approve, setApprove] = useState<'approved' | 'changes_requested' | null>(null);
  const [blocked, setBlocked] = useState<{ title: string; items: Issue[] } | null>(null);
  const poll = useRef<ReturnType<typeof setInterval>>(undefined);

  const loadExports = useCallback(() => get<{ exports: ExportRow[] }>(`/api/projects/${project.id}/exports`).then((r) => setExports(r.exports)).catch(() => undefined), [project.id]);
  const loadHistory = useCallback(() => get<{ revisions: Revision[]; events: HistoryEvent[] }>(`/api/projects/${project.id}/revisions`).then(setHistory).catch(() => undefined), [project.id]);
  useEffect(() => { void loadExports(); void loadHistory(); }, [loadExports, loadHistory, project.version]);
  useEffect(() => {
    const running = exports.some((e) => e.status === 'queued' || e.status === 'running');
    clearInterval(poll.current);
    if (running) poll.current = setInterval(() => { void loadExports().then(() => undefined); }, 700);
    return () => clearInterval(poll.current);
  }, [exports, loadExports]);
  useEffect(() => {
    // refresh project status when an export completes
    if (exports.some((e) => e.status === 'done')) get<{ project: typeof project }>(`/api/projects/${project.id}`).then((r) => useEditor.setState((s) => ({ project: s.project ? { ...s.project, status: r.project.status, approvedVersion: r.project.approvedVersion, approvalCurrent: r.project.approvalCurrent } : null }))).catch(() => undefined);
  }, [exports.filter((e) => e.status === 'done').length]); // eslint-disable-line react-hooks/exhaustive-deps

  const goTo = (i: Issue) => {
    if (i.pageKey) useEditor.setState({ page: i.pageKey, view: 'single', selected: i.elementId ?? null, scope: 'page' });
    else if (i.code.includes('events')) useEditor.setState({ stage: 3 });
  };
  const ack = (ids: string[], on: boolean) => edit((d) => ({ ...d, checks: { ...d.checks, acknowledged: on ? [...new Set([...d.checks.acknowledged, ...ids])] : d.checks.acknowledged.filter((x) => !ids.includes(x)) } }), 'ack');

  const startExport = async (kind: ExportRow['kind']) => {
    const ok = await flushPendingSave();
    const s = useEditor.getState();
    if (!ok || s.saveState !== 'saved') { toast('Save the project before exporting (exports use a saved revision).', 'err'); return; }
    try {
      await post(`/api/projects/${project.id}/exports`, { kind, version: s.baseVersion });
      toast(`${KIND_LABEL[kind]} started from revision ${s.baseVersion}. You can keep editing.`, 'info');
      void loadExports();
    } catch (e) {
      if (e instanceof ApiError && e.status === 422) setBlocked({ title: e.message, items: (e.details as Issue[]) ?? [] });
      else toast(errorMessage(e), 'err');
    }
  };

  const st = STATUS[project.status] ?? STATUS.draft;
  const back = backingPage(doc);
  const plan = impositionPlan(doc);
  const imp = plan.imp;
  const sheets = plan.sheets + (plan.carrier?.sheets ?? 0);
  const isTent = doc.product.productType === 'tent';
  const prodPages = isTent ? 1 : printOrder(doc).length;
  return (
    <div>
      <div className="section col">
        <h2>4 · Review & export</h2>
        <div className="row wrap">
          <span className={`badge ${st.cls}`}>{st.label}</span>
          {project.approvedVersion && (project.approvalCurrent
            ? <span className="badge ok">Customer approved revision {project.approvedVersion}</span>
            : <span className="badge warn">Approval of rev {project.approvedVersion} no longer current — artwork changed</span>)}
        </div>
      </div>

      <div className="section col">
        <div className="row"><h3 className="grow" style={{ margin: 0 }}>Artwork checks</h3>
          <span className={`badge ${errors.length ? 'err' : 'ok'}`}>{errors.length} blocking</span>
          <span className={`badge ${unacked.length ? 'warn' : ''}`}>{warnings.length} warnings</span>
        </div>
        {errors.length === 0 && warnings.length === 0 && <div className="notice ok small">No issues found on any page.</div>}
        {errors.length > 0 && <p className="small muted" style={{ margin: 0 }}>Blocking errors must be fixed before production export. Click an issue to open it.</p>}
        <div className="col" style={{ gap: 6 }}>
          {[...errors, ...warnings, ...issues.filter((i) => i.severity === 'info')].map((i) => (
            <div key={i.id} className={`issue ${acked.has(i.id) ? 'acked' : ''}`}>
              <span className={`ic ${i.severity}`} aria-hidden>{i.severity === 'error' ? '!' : i.severity === 'warning' ? '!' : 'i'}</span>
              <button className="grow" style={{ border: 0, background: 'none', textAlign: 'left', padding: 0, cursor: 'pointer' }} onClick={() => goTo(i)}>
                <strong>{i.pageKey ? pageInfo(doc, i.pageKey).label + ': ' : 'Project: '}</strong>{i.message}
              </button>
              {i.severity === 'warning' && (
                <label className="check small" title="Acknowledge: you reviewed this and accept it for print">
                  <input type="checkbox" checked={acked.has(i.id)} onChange={(e) => ack([i.id], e.target.checked)} />OK
                </label>
              )}
            </div>
          ))}
        </div>
        {unacked.length > 1 && <button className="btn sm" onClick={() => ack(unacked.map((w) => w.id), true)}>Acknowledge all {unacked.length} warnings</button>}
        <details className="adv">
          <summary>Image resolution rules</summary>
          <div className="grid2">
            <NumberField label="Recommended (warning below)" unit="ppi" value={doc.checks.ppiTarget} min={50} max={1200} digits={0} onChange={(v) => edit((d) => ({ ...d, checks: { ...d.checks, ppiTarget: v } }), 'ppi')} />
            <NumberField label="Minimum (blocking below)" unit="ppi" value={doc.checks.ppiMinimum} min={0} max={1200} digits={0} onChange={(v) => edit((d) => ({ ...d, checks: { ...d.checks, ppiMinimum: v } }), 'ppi')} />
          </div>
        </details>
      </div>

      <div className="section col">
        <h3>Export</h3>
        <button className="btn" onClick={() => startExport('proof')}>Customer proof PDF</button>
        <button className="btn primary" disabled={errors.length > 0} onClick={() => startExport('production')}>{isTent ? `Production PDF, flat tent (single-up${doc.product.tent.layout && doc.product.tent.layout !== 'classic' ? '' : ', fold marks'})` : `Production PDF, single-up (${prodPages} page${prodPages === 1 ? '' : 's'}${doc.product.duplex ? ', front/back order' : ''})`}</button>
        <button className="btn" disabled={errors.length > 0 || !imp} onClick={() => startExport('imposed')} title={imp ? undefined : 'Not imposed: no bleed, or larger than SRA3'}>
          {imp ? `Imposed SRA3 PDF (${imp.perSheet}-up, ${sheets} sheet${sheets === 1 ? '' : 's'})` : 'Imposed SRA3 PDF (not available for this size)'}
        </button>
        {doc.product.productType !== 'year' && <button className="btn" disabled={errors.length > 0} onClick={() => startExport('pages')}>{isTent ? 'Separate face PDFs (ZIP)' : 'Separate page PDFs (ZIP)'}</button>}
        {back && <button className="btn" disabled={errors.length > 0} onClick={() => startExport('backing')}>{back.key === 'carrier' ? 'Carrier card PDF (single-up)' : doc.product.productType === 'year' ? 'Reverse side PDF (single-up)' : 'Backing-board artwork PDF'}</button>}
        <ImpositionNote doc={doc} />
        {!project.approvalCurrent && <p className="small muted" style={{ margin: 0 }}>Production files can be made before approval; the status above shows whether the current artwork is approved.</p>}
        <p className="small faint" style={{ margin: 0 }}>Exports render from the latest saved revision ({useEditor.getState().baseVersion}){saveState !== 'saved' ? ' — unsaved changes will be saved first' : ''}. Screen previews and proof PDFs are not colour proofs.</p>
        {exports.length > 0 && (
          <div className="col" style={{ gap: 6 }}>
            {exports.slice(0, 8).map((e) => (
              <div key={e.id} className="card" style={{ padding: 8 }}>
                <div className="row small">
                  <strong className="grow">{KIND_LABEL[e.kind]} · rev {e.version}</strong>
                  <span className={`badge ${e.status === 'done' ? 'ok' : e.status === 'failed' ? 'err' : e.status === 'cancelled' ? '' : 'info'}`}>{e.status}</span>
                </div>
                {(e.status === 'running' || e.status === 'queued') && <div className="progress" style={{ margin: '6px 0' }}><div style={{ width: `${e.progress * 100}%` }} /></div>}
                <div className="row small">
                  <span className="grow faint">{e.message}{e.durationMs ? ` · ${(e.durationMs / 1000).toFixed(1)} s` : ''}{e.bytes ? ` · ${(e.bytes / 1e6).toFixed(1)} MB` : ''}</span>
                  {(e.status === 'running' || e.status === 'queued') && <button className="btn sm" onClick={() => del(`/api/exports/${e.id}`).then(loadExports).catch((x) => toast(errorMessage(x), 'err'))}>Cancel</button>}
                  {(e.status === 'failed' || e.status === 'cancelled') && <button className="btn sm" onClick={() => post(`/api/exports/${e.id}/retry`).then(loadExports).catch((x) => { if (x instanceof ApiError && x.status === 422) setBlocked({ title: x.message, items: x.details as Issue[] }); else toast(errorMessage(x), 'err'); })}>Retry</button>}
                  {e.status === 'done' && e.hasFile && <a className="btn sm primary" href={`/api/exports/${e.id}/file`} download>Download</a>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="section col">
        <h3>Customer approval</h3>
        <p className="small muted" style={{ margin: 0 }}>Send the customer a proof link to approve online, or record their response yourself. Approval is for one saved revision; any later change to the artwork cancels it.</p>
        <ProofLinks projectId={project.id} version={project.version} />
        <div className="row wrap">
          <button className="btn sm" onClick={async () => { if (await flushPendingSave()) setApprove('approved'); }}>Record approval…</button>
          <button className="btn sm" onClick={async () => { if (await flushPendingSave()) setApprove('changes_requested'); }}>Changes requested…</button>
        </div>
      </div>

      <div className="section col">
        <h3>Revisions & history</h3>
        <div className="row">
          <button className="btn sm" onClick={async () => {
            const name = prompt('Name this revision (e.g. "Sent to customer 4 Oct")');
            if (name) { const ok = await save('manual', name); if (ok) { toast('Named revision saved', 'ok'); void loadHistory(); } }
          }}>Save named revision…</button>
          <a className="btn sm" href={`/api/projects/${project.id}/package`} download onClick={() => void flushPendingSave()}>Download editable backup</a>
        </div>
        <div className="col" style={{ gap: 4, maxHeight: 260, overflow: 'auto' }}>
          {history.revisions.map((r) => (
            <div key={r.version} className="row small">
              <span className="mono" style={{ width: 36 }}>r{r.version}</span>
              <span className="grow">{r.name ? <strong>{r.name}</strong> : r.kind}<span className="faint"> · {new Date(r.createdAt).toLocaleString()}{r.createdBy ? ` · ${r.createdBy}` : ''}</span></span>
              {r.version !== project.version && <button className="btn sm ghost" onClick={async () => {
                if (!confirm(`Restore revision ${r.version}? The current design is replaced (you can undo).`)) return;
                const { doc: old } = await get<{ doc: ProjectDoc }>(`/api/projects/${project.id}/revisions/${r.version}`);
                edit(() => old, 'restore');
                toast(`Revision ${r.version} restored into the editor. It will be saved as a new revision.`, 'ok');
              }}>Restore</button>}
            </div>
          ))}
        </div>
        {history.events.length > 0 && (
          <details className="adv"><summary>Status history ({history.events.length})</summary>
            <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
              {history.events.map((e, i) => <li key={i}><strong>{e.type.replace(/_/g, ' ')}</strong>{e.version ? ` (rev ${e.version})` : ''} · {new Date(e.createdAt).toLocaleString()}{e.userName ? ` · ${e.userName}` : ''}{e.note ? ` — ${e.note}` : ''}</li>)}
            </ul>
          </details>
        )}
      </div>

      <div className="section">
        <details className="adv">
          <summary>Colour & production handoff</summary>
          <div className="small muted col">
            <p>Text, lines and panels are written to the PDF exactly as defined: RGB values as RGB, CMYK values (e.g. K-only black) as DeviceCMYK. Photographs stay RGB (sRGB); they are not converted to CMYK.</p>
            <p>The PDF has correct TrimBox/BleedBox and embedded fonts but is not PDF/X certified and carries no output intent. For colour-managed print, run it through the press preflight / ICC conversion (e.g. PitStop, Acrobat Preflight or the RIP) with the press profile.</p>
          </div>
        </details>
      </div>

      {approve && <ApprovalDialog kind={approve} onClose={() => { setApprove(null); void loadHistory(); }} errors={errors.length} />}
      {blocked && (
        <Modal title="Export blocked" onClose={() => setBlocked(null)} footer={<button className="btn primary" onClick={() => setBlocked(null)}>OK</button>}>
          <p>{blocked.title}</p>
          <div className="col" style={{ gap: 6 }}>
            {blocked.items.slice(0, 30).map((i) => (
              <button key={i.id} className="issue" onClick={() => { goTo(i); setBlocked(null); }}>
                <span className={`ic ${i.severity}`}>!</span><span><strong>{i.pageKey ? pageInfo(doc, i.pageKey).label + ': ' : ''}</strong>{i.message}</span>
              </button>
            ))}
          </div>
          {blocked.items.some((i) => i.severity === 'warning') && <p className="small muted">Review each warning and tick “OK” in Artwork checks to acknowledge it.</p>}
        </Modal>
      )}
    </div>
  );
}

function ApprovalDialog({ kind, onClose, errors }: { kind: 'approved' | 'changes_requested'; onClose: () => void; errors: number }) {
  const project = useEditor((s) => s.project)!;
  const version = useEditor((s) => s.baseVersion);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={kind === 'approved' ? `Record approval of revision ${version}` : `Record changes requested (revision ${version})`} onClose={onClose}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn primary" disabled={busy || (kind === 'approved' && (!note.trim() || errors > 0))} onClick={async () => {
        setBusy(true);
        try {
          await post(`/api/projects/${project.id}/status`, { action: kind, version, note });
          useEditor.setState((s) => ({ project: s.project ? { ...s.project, status: kind, approvedVersion: kind === 'approved' ? version : s.project.approvedVersion, approvalCurrent: kind === 'approved' ? true : s.project.approvalCurrent } : null }));
          toast(kind === 'approved' ? `Approval of revision ${version} recorded` : 'Recorded', 'ok');
          onClose();
        } catch (e) { toast(errorMessage(e), 'err'); } finally { setBusy(false); }
      }}>{busy ? <Spinner /> : 'Record'}</button></>}>
      {kind === 'approved' && errors > 0 && <div className="notice err small">Fix the {errors} blocking issue(s) first.</div>}
      <Field label={kind === 'approved' ? 'How did the customer approve? (required)' : 'What changes were requested?'}>
        <textarea className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={kind === 'approved' ? 'e.g. Email from Jane Smith, 4 Oct 2026, approving proof r12' : 'e.g. Swap March and April photos'} />
      </Field>
      <p className="small muted">This records approval of the exact saved artwork (revision {version}). Editing afterwards automatically marks the approval as no longer current.</p>
    </Modal>
  );
}

interface ProofLink { token: string; version: number; createdAt: string; expiresAt: string; createdBy: string | null; revoked: boolean; expired: boolean; decision: 'approved' | 'changes_requested' | null; decidedBy: string | null; decisionNote: string | null; decidedAt: string | null }

/** Customer proof links: create, copy, see the customer's decision, revoke. */
function ProofLinks({ projectId, version }: { projectId: string; version: number }) {
  const [links, setLinks] = useState<ProofLink[]>([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => get<{ links: ProofLink[] }>(`/api/projects/${projectId}/proof-links`).then((r) => setLinks(r.links)).catch(() => undefined), [projectId]);
  useEffect(() => { void load(); }, [load, version]);
  const abs = (token: string) => `${location.origin}/proof/${token}`;
  const copy = async (token: string) => {
    try { await navigator.clipboard.writeText(abs(token)); toast('Proof link copied. Paste it into your email to the customer.', 'ok'); }
    catch { prompt('Copy this link for the customer', abs(token)); }
  };
  const open = links.filter((l) => !l.revoked && !l.expired);
  return (
    <div className="col" style={{ gap: 6 }}>
      <button className="btn sm" style={{ alignSelf: 'flex-start' }} disabled={busy} onClick={async () => {
        setBusy(true);
        try {
          if (!(await flushPendingSave())) return;
          const r = await post<{ token: string }>(`/api/projects/${projectId}/proof-links`);
          await load();
          await copy(r.token);
        } catch (e) { toast(errorMessage(e), 'err'); } finally { setBusy(false); }
      }}>{busy ? 'Creating…' : 'Create customer proof link'}</button>
      {open.length > 0 && (
        <div className="col" style={{ gap: 4 }} aria-label="Proof links">
          {open.map((l) => (
            <div key={l.token} className="row small wrap" data-proof-link={l.token}>
              <span className="mono">r{l.version}</span>
              <span className="grow">
                {l.decision ? <span className={`badge ${l.decision === 'approved' ? 'ok' : 'warn'}`}>{l.decision === 'approved' ? 'Approved' : 'Changes requested'} by {l.decidedBy}</span>
                  : l.version === version ? <span className="badge info">Waiting for the customer</span> : <span className="badge">Artwork changed since</span>}
                {l.decisionNote && <span className="faint"> “{l.decisionNote}”</span>}
              </span>
              {!l.decision && <button className="btn sm ghost" onClick={() => copy(l.token)}>Copy link</button>}
              <button className="btn sm ghost danger" onClick={async () => { try { await del(`/api/proof-links/${l.token}`); void load(); } catch (e) { toast(errorMessage(e), 'err'); } }}>Revoke</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
